/**
 * Terms of Service Page
 * © 2026 A-1 Affiliate Marketing LLC - All Rights Reserved
 */

import React from 'react';
import MachineTranslationNotice from './MachineTranslationNotice';
import './LegalPage.css';

const TermsOfService = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">
        <button onClick={() => window.history.back()} className="back-btn">← Back</button>

        <MachineTranslationNotice />
        
        <h1>TERMS OF SERVICE</h1>
        <p className="subtitle">SuperBrain Agentic Aggregate</p>
        <p className="effective-date">Effective Date: April 1, 2026</p>
        <p className="copyright">© 2026 A-1 Affiliate Marketing LLC. All Rights Reserved.</p>

        <section>
          <h2>1. Acceptance of Terms</h2>
          <p>By accessing or using SuperBrain Agentic Aggregate ("Service"), you agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access the Service.</p>
        </section>

        <section>
          <h2>2. Intellectual Property Rights</h2>
          <h3>2.1 Ownership</h3>
          <p>All content, features, functionality, code, designs, graphics, and trademarks of the Service are owned exclusively by A-1 Affiliate Marketing LLC and are protected by United States and international copyright, trademark, patent, trade secret, and other intellectual property laws.</p>
          
          <h3>2.2 License Grant</h3>
          <p>Subject to your compliance with these Terms, we grant you a limited, non-exclusive, non-transferable, non-sublicensable, revocable license to access and use the Service for your personal or internal business purposes.</p>
          
          <h3>2.3 Restrictions</h3>
          <p>You may NOT:</p>
          <ul>
            <li>Copy, modify, distribute, sell, or lease any part of our Service</li>
            <li>Reverse engineer or attempt to extract the source code</li>
            <li>Create derivative works based on the Service</li>
            <li>Use the Service to build a competing product</li>
            <li>Remove any copyright or proprietary notations</li>
          </ul>
        </section>

        <p className="non-affiliation-notice">
          Not affiliated with any government agency, AI company or search engine.
        </p>

        <section>
          <h2>3. Service Plans & Billing</h2>
          <h3>3.1 Free Trial</h3>
          <p>Eligible new accounts receive a <strong>3-day free trial</strong>. A payment method is collected when you start the trial. Your selected subscription begins automatically when the trial ends unless you cancel before the end of the trial; no subscription charge is due when you cancel in time.</p>

          <h3>3.2 Subscription Plans</h3>
          <ul>
            <li><strong>Professional</strong>: $59/month or $590/year — 225 queries/month, 25 image generations</li>
            <li><strong>Business</strong>: $99/month or $990/year — 400 queries/month, 75 image generations</li>
            <li><strong>Enterprise</strong>: $249/month or $2,490/year — 1,000 queries/month, 250 image generations</li>
            <li><strong>Ultimate</strong>: $499/month or $4,990/year — 2,000 queries/month, 750 image generations, account manager</li>
          </ul>
          <p>Yearly plans include 2 months free vs the monthly rate.</p>
          <p>Bundle discounts cannot be combined with other discounts or promotion codes.</p>

          <h3>3.3 Payment</h3>
          <p>All payments are processed through Stripe. You authorize us to charge your payment method for your selected plan.</p>

          <h3>3.4 Auto-Renewal</h3>
          <p>Subscriptions renew automatically on the monthly or yearly cadence and at the price shown when you subscribe, until canceled. You can cancel from your account dashboard at any time. Cancellation stops future renewals; paid access continues through the end of the current billing period.</p>

          <h3>3.5 Refunds and Trial Cancellation</h3>
          <p>Cancel before the 3-day trial ends to avoid the first subscription charge. For monthly plans, you may request a full refund of the most recent subscription charge within 7 days if usage in that billing period is no more than 1,000 queries. For yearly plans, you may request a prorated refund within 30 days of purchase. Monthly cancellations do not receive prorated refunds for the unused part of a billing period. Full eligibility and request instructions are in the <a href="/refund">Refund Policy</a>. These terms do not limit rights that cannot be waived under applicable law.</p>

          <h3>3.6 Billing Questions</h3>
          <p>Refund requests should be sent to <a href="mailto:a1affiliatemarketingllc@gmail.com">a1affiliatemarketingllc@gmail.com</a>. Approved refunds are returned to the original payment method; processing time depends on the payment provider.</p>
        </section>

        <section>
          <h2>4. Usage Limits</h2>
          <p>Each plan has monthly query limits. Exceeding your limit will result in service suspension until the next billing cycle or plan upgrade.</p>
        </section>

        <section>
          <h2>5. Prohibited Uses</h2>
          <p>You agree NOT to use the Service to:</p>
          <ul>
            <li>Violate any laws or regulations</li>
            <li>Infringe on intellectual property rights</li>
            <li>Transmit harmful or malicious code</li>
            <li>Harass, abuse, or harm another person</li>
            <li>Impersonate any person or entity</li>
            <li>Engage in unauthorized framing or linking</li>
          </ul>
        </section>

        <section>
          <h2>6. Termination</h2>
          <p>We reserve the right to terminate or suspend your access immediately, without prior notice, for:</p>
          <ul>
            <li>Breach of these Terms</li>
            <li>Fraudulent activity</li>
            <li>Non-payment</li>
            <li>Any reason at our sole discretion</li>
          </ul>
        </section>

        <section>
          <h2>7. Disclaimer of Warranties</h2>
          <p className="disclaimer">THE SERVICE, INCLUDING ALL AI-GENERATED OUTPUTS, IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, EITHER EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, ACCURACY, RELIABILITY, NON-INFRINGEMENT, OR UNINTERRUPTED OPERATION.</p>
          <p className="disclaimer">SUPERBRAIN OUTPUTS DO NOT CONSTITUTE LEGAL, MEDICAL, FINANCIAL, MENTAL-HEALTH, TAX, OR SAFETY ADVICE. YOU MUST INDEPENDENTLY VERIFY ALL INFORMATION BEFORE ACTING ON IT.</p>
        </section>

        <section>
          <h2>8. Limitation of Liability & Indemnification</h2>
          <h3>8.1 NO LIABILITY FOR AI OUTPUTS</h3>
          <p className="disclaimer">A-1 AFFILIATE MARKETING LLC SHALL NOT BE LIABLE FOR:</p>
          <ul>
            <li>Any decisions made based on AI-generated responses</li>
            <li>Accuracy, completeness, or reliability of AI outputs</li>
            <li>Professional advice (legal, medical, financial, etc.)</li>
            <li>Business decisions or financial losses</li>
            <li>Indirect, incidental, special, consequential, or punitive damages</li>
            <li>Loss of profits, data, or business opportunities</li>
          </ul>
          
          <h3>8.2 AI RESPONSE DISCLAIMER</h3>
          <p className="disclaimer">
            AI responses from Claude, GPT-5, Gemini, Grok, Perplexity, and Mistral are INFORMATIONAL ONLY. 
            They do NOT constitute professional advice of any kind. Users are solely responsible for verifying 
            information and making their own decisions.
          </p>
          
          <h3>8.3 USER INDEMNIFICATION</h3>
          <p>You agree to indemnify, defend, and hold harmless A-1 Affiliate Marketing LLC from any claims, 
          damages, losses, or expenses arising from:</p>
          <ul>
            <li>Your use or misuse of the Service</li>
            <li>Decisions made based on AI outputs</li>
            <li>Violation of these Terms</li>
            <li>Violation of any laws or third-party rights</li>
          </ul>
          
          <h3>8.4 MAXIMUM LIABILITY</h3>
          <p className="disclaimer">
            IN NO EVENT SHALL OUR TOTAL LIABILITY EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE 
            PAST 12 MONTHS, OR $100, WHICHEVER IS LESS.
          </p>
        </section>

        <section>
          <h2>9. Copyright Infringement</h2>
          <p>If you believe content on our Service infringes your copyright, contact us at: <a href="mailto:legal@superbrain.life">legal@superbrain.life</a></p>
        </section>

        <section>
          <h2>10. Governing Law, Arbitration & Class-Action Waiver</h2>

          <h3>10.1 Governing Law</h3>
          <p>These Terms shall be governed by and construed in accordance with the laws of the <strong>State of Kansas, United States</strong>, without regard to its conflict-of-laws principles. The United Nations Convention on Contracts for the International Sale of Goods does not apply.</p>

          <h3>10.2 Binding Arbitration</h3>
          <p>Except where prohibited by applicable consumer-protection law, <strong>any dispute, claim, or controversy arising out of or relating to these Terms or your use of the Service shall be resolved exclusively by binding individual arbitration</strong> administered by the American Arbitration Association (AAA) under its Consumer Arbitration Rules. The arbitration shall be conducted in <strong>Kansas</strong>, in English, by a single arbitrator. Judgment on the award may be entered in any court of competent jurisdiction.</p>

          <h3>10.3 Class-Action Waiver</h3>
          <p className="disclaimer">YOU AND A-1 AFFILIATE MARKETING LLC EACH AGREE THAT ALL CLAIMS MUST BE BROUGHT IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS, CONSOLIDATED, OR REPRESENTATIVE ACTION. THE ARBITRATOR MAY NOT CONSOLIDATE OR JOIN MORE THAN ONE PERSON'S CLAIMS.</p>

          <h3>10.4 Exclusive Venue (if Arbitration Doesn't Apply)</h3>
          <p>If arbitration is found unenforceable for any reason, you and A-1 Affiliate Marketing LLC submit to the exclusive personal jurisdiction and venue of the state and federal courts located in <strong>Kansas</strong>, and waive any objection based on inconvenient forum.</p>

          <h3>10.5 30-Day Opt-Out</h3>
          <p>You may opt out of the arbitration and class-action waiver in Sections 10.2 and 10.3 by sending written notice within 30 days of first accepting these Terms to <a href="mailto:legal@superbrain.life">legal@superbrain.life</a> with the subject line "Arbitration Opt-Out" and your full legal name and account email.</p>

          <h3>10.6 Force Majeure</h3>
          <p>Neither party is liable for any failure or delay caused by events beyond its reasonable control, including acts of God, natural disasters, war, terrorism, pandemics, civil unrest, government actions, internet or third-party provider outages (including any failure of the underlying AI model providers), labor disputes, or shortages of materials.</p>

          <h3>10.7 EU / UK / California Consumer Rights</h3>
          <p>If you reside in the European Union, the United Kingdom, California, or any other jurisdiction whose mandatory consumer-protection laws grant you rights that cannot be waived by contract, those rights are preserved to the extent legally required, and only to that extent — all other terms in these Terms remain in full force and effect.</p>

          <h3>10.8 Language — English Controls</h3>
          <p>These Terms may be made available in other languages as a courtesy translation. In the event of any conflict or inconsistency between a translation and the English version, <strong>the English-language version shall control and be the sole legally binding version.</strong></p>

          <h3>10.9 Severability</h3>
          <p>If any provision of these Terms is found unenforceable, the remaining provisions remain in full force and effect, and the unenforceable provision shall be reformed only to the extent necessary to make it enforceable.</p>
        </section>

        <section>
          <h2>11. Changes to Terms</h2>
          <p>We reserve the right to modify these Terms at any time. Continued use of the Service after changes constitutes acceptance.</p>
        </section>

        <section>
          <h2>12. Contact Information</h2>
          <p><strong>A-1 Affiliate Marketing LLC</strong></p>
          <p>Email: <a href="mailto:a1affiliatemarketingllc@gmail.com">a1affiliatemarketingllc@gmail.com</a></p>
          <p>Website: https://superbrain.life</p>
        </section>

        <div className="footer-blessing">
          <p><strong>Built with Faith, Service, and Integrity</strong></p>
          <p>Catholic Owned • USMC Veteran Owned</p>
          <p>✝ GOD BLESS EVERYONE ✝</p>
        </div>
      </div>
    </div>
  );
};

export default TermsOfService;
